/** PRF secrets are browser-only. The server receives ciphertext and public keys. */
export const PASSKEY_PRF_INPUT = new TextEncoder().encode('yada:passkey-recovery:prf:v1');

export interface PasskeyVault {
  version: 1;
  publicKey: string;
  salt: string;
  iv: string;
  ciphertext: string;
}

export function toBase64(value: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(value)));
}
export function fromBase64(value: string): ArrayBuffer {
  return Uint8Array.from(atob(value), c => c.charCodeAt(0)).buffer;
}

async function wrappingKey(prf: ArrayBuffer, salt: ArrayBuffer) {
  if (prf.byteLength !== 32) throw new Error('Passkey did not provide a valid encryption secret.');
  const key = await crypto.subtle.importKey('raw', prf, 'HKDF', false, ['deriveKey']);
  return crypto.subtle.deriveKey({ name: 'HKDF', hash: 'SHA-256', salt,
    info: new TextEncoder().encode('yada:passkey-recovery:wrapping:v1') }, key,
  { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}
function binding(accountId: string, credentialId: string, publicKey: string) {
  return new TextEncoder().encode(JSON.stringify(['yada:passkey-vault:v1', accountId, credentialId, publicKey]));
}

export async function createPasskeyVault(prf: ArrayBuffer, accountId: string, credentialId: string) {
  const pair = await crypto.subtle.generateKey('X25519', true, ['deriveKey', 'deriveBits']) as CryptoKeyPair;
  const privateBytes = await crypto.subtle.exportKey('pkcs8', pair.privateKey);
  const publicKey = toBase64(await crypto.subtle.exportKey('raw', pair.publicKey));
  const salt = crypto.getRandomValues(new Uint8Array(32));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  try {
    const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv,
      additionalData: binding(accountId, credentialId, publicKey) }, await wrappingKey(prf, salt.buffer), privateBytes);
    return { vault: { version: 1 as const, publicKey, salt: toBase64(salt.buffer),
      iv: toBase64(iv.buffer), ciphertext: toBase64(ciphertext) }, publicKey: pair.publicKey };
  } finally { new Uint8Array(privateBytes).fill(0); }
}

export async function unlockPasskeyVault(vault: PasskeyVault, prf: ArrayBuffer,
  accountId: string, credentialId: string): Promise<CryptoKey> {
  if (vault.version !== 1) throw new Error('Unsupported passkey recovery version.');
  const bytes = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromBase64(vault.iv),
    additionalData: binding(accountId, credentialId, vault.publicKey) },
  await wrappingKey(prf, fromBase64(vault.salt)), fromBase64(vault.ciphertext));
  try { return await crypto.subtle.importKey('pkcs8', bytes, 'X25519', false, ['deriveKey', 'deriveBits']); }
  finally { new Uint8Array(bytes).fill(0); }
}
